import {
  API_ROUTES,
  formatMinor,
  profitAndLossRowSchema,
  type ProfitAndLossRow,
} from '@hvac/contracts';
import { Card, CardBody, CardHeader, Skeleton } from '@hvac/ui';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { apiClient } from '../../shared/api/queries.js';

/**
 * The profit and loss statement.
 *
 * Two columns, on purpose. "Signed" is what a person has put their name to.
 * "Including unsigned" is what the books will say once the waiting records are
 * signed. Showing only the first makes a new client's books look empty;
 * showing only the second claims a certainty nobody has given. The gap
 * between them is the work left, and it is worth seeing.
 */

export type PeriodKey = 'this_year' | 'this_quarter' | 'last_month' | 'last_year';

const PERIODS: readonly { key: PeriodKey; label: string }[] = [
  { key: 'this_year', label: 'This year' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_year', label: 'Last year' },
];

const iso = (date: Date): string => date.toISOString().slice(0, 10);

export function bounds(key: PeriodKey, today = new Date()): { from: string; to: string } {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  switch (key) {
    case 'this_year':
      return { from: `${String(y)}-01-01`, to: iso(today) };
    case 'this_quarter': {
      const start = Math.floor(m / 3) * 3;
      return { from: iso(new Date(Date.UTC(y, start, 1))), to: iso(today) };
    }
    case 'last_month':
      return {
        from: iso(new Date(Date.UTC(y, m - 1, 1))),
        to: iso(new Date(Date.UTC(y, m, 0))),
      };
    case 'last_year':
      return { from: `${String(y - 1)}-01-01`, to: `${String(y - 1)}-12-31` };
  }
}

const pnlQuery = (organizationId: string, from: string, to: string) =>
  queryOptions({
    queryKey: ['reports', organizationId, 'pnl', from, to] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.reports.profitAndLoss(organizationId)}?from=${from}&to=${to}`,
        schema: z.array(profitAndLossRowSchema),
        signal,
      }),
    staleTime: 60_000,
  });

const SECTION_TITLES: Readonly<Record<ProfitAndLossRow['section'], string>> = {
  revenue: 'Revenue',
  cost_of_goods_sold: 'Cost of goods sold',
  expense: 'Operating expenses',
  outside: 'Spent, but not on the P&L',
};

const total = (rows: readonly ProfitAndLossRow[], withUnsigned: boolean): bigint =>
  rows.reduce(
    (sum, row) => sum + BigInt(row.signedMinor) + (withUnsigned ? BigInt(row.unsignedMinor) : 0n),
    0n,
  );

export function ProfitAndLossCard({ organizationId }: { organizationId: string }) {
  const [period, setPeriod] = useState<PeriodKey>('this_year');
  const { from, to } = bounds(period);
  const statement = useQuery(pnlQuery(organizationId, from, to));
  const rows = statement.data ?? [];

  const bySection = (section: ProfitAndLossRow['section']) =>
    rows.filter((row) => row.section === section);
  const revenue = bySection('revenue');
  const cogs = bySection('cost_of_goods_sold');
  const expenses = bySection('expense');
  const outside = bySection('outside');

  const net = (withUnsigned: boolean) =>
    total(revenue, withUnsigned) - total(cogs, withUnsigned) - total(expenses, withUnsigned);
  const unsignedAny = rows.some((row) => row.unsignedMinor !== '0');
  const disagreements = rows.reduce((sum, row) => sum + row.disagreements, 0);

  return (
    <Card>
      <CardHeader
        title="Profit and loss"
        description={
          statement.data === undefined
            ? 'Computing…'
            : describe(net(false), net(true), unsignedAny, revenue.length === 0)
        }
      />
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Period">
          {PERIODS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={period === option.key}
              onClick={() => setPeriod(option.key)}
              className={[
                'rounded-[var(--radius-control)] px-3 py-1 text-sm',
                period === option.key
                  ? 'bg-brand-50 font-medium text-brand-700'
                  : 'text-content-muted hover:bg-surface-sunken',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>

        {statement.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nothing is recorded between {from} and {to}.
          </p>
        ) : (
          <table className="w-full text-sm tabular">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Account</th>
                <th className="py-1 text-right font-medium">Signed</th>
                <th className="py-1 text-right font-medium">Including unsigned</th>
              </tr>
            </thead>
            <tbody>
              <Section title={SECTION_TITLES.revenue} rows={revenue} />
              <Section title={SECTION_TITLES.cost_of_goods_sold} rows={cogs} />
              <TotalRow
                label="Gross profit"
                signed={total(revenue, false) - total(cogs, false)}
                all={total(revenue, true) - total(cogs, true)}
              />
              <Section title={SECTION_TITLES.expense} rows={expenses} />
              <TotalRow label="Net income" signed={net(false)} all={net(true)} strong />
              {outside.length === 0 ? null : (
                <Section title={SECTION_TITLES.outside} rows={outside} muted />
              )}
            </tbody>
          </table>
        )}

        {disagreements === 0 ? null : (
          <p className="text-xs text-warning">
            {disagreements} line{disagreements === 1 ? '' : 's'} sit in an account that disagrees
            with how {disagreements === 1 ? 'it is' : 'they are'} treated for tax. Placed by
            account; worth a look before this statement is relied on.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function describe(signed: bigint, all: bigint, unsignedAny: boolean, noRevenue: boolean): string {
  const phrase = (value: bigint) =>
    value < 0n ? `a loss of ${formatMinor((-value).toString())}` : formatMinor(value.toString());
  if (noRevenue) {
    return 'No income is recorded yet, so this is spending only. Income arrives with bank deposits and invoices.';
  }
  if (!unsignedAny) return `Net income ${phrase(signed)}, all of it signed.`;
  return `Net income ${phrase(signed)} signed, ${phrase(all)} once the waiting records are signed.`;
}

function Section({
  title,
  rows,
  muted = false,
}: {
  title: string;
  rows: readonly ProfitAndLossRow[];
  muted?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <>
      <tr>
        <td colSpan={3} className="pt-3 pb-1 text-xs font-medium uppercase text-content-muted">
          {title}
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.accountId} className={muted ? 'text-content-muted' : 'text-content'}>
          <td className="py-0.5">
            <span className="text-content-muted">{row.accountCode}</span> {row.accountName}
          </td>
          <td className="py-0.5 text-right">{formatMinor(row.signedMinor)}</td>
          <td className="py-0.5 text-right">
            {formatMinor((BigInt(row.signedMinor) + BigInt(row.unsignedMinor)).toString())}
          </td>
        </tr>
      ))}
    </>
  );
}

function TotalRow({
  label,
  signed,
  all,
  strong = false,
}: {
  label: string;
  signed: bigint;
  all: bigint;
  strong?: boolean;
}) {
  return (
    <tr className={['border-t border-border', strong ? 'font-semibold' : 'font-medium'].join(' ')}>
      <td className="py-1">{label}</td>
      <td className={['py-1 text-right', signed < 0n ? 'text-danger' : ''].join(' ')}>
        {formatMinor(signed.toString())}
      </td>
      <td className={['py-1 text-right', all < 0n ? 'text-danger' : ''].join(' ')}>
        {formatMinor(all.toString())}
      </td>
    </tr>
  );
}
